import { Box, SxProps, Theme } from "@outschool/backpack";
import { ApolloError } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { ReadOnlyBanner } from "@outschool/ui-components-classroom";
import { LazyBox } from "@outschool/ui-components-shared";
import { lazyWithRetry } from "@outschool/ui-utils";
import React, { Suspense } from "react";

import * as Env from "../../../shared/Env";
import SectionStartReminder from "../../containers/SectionStartReminder";
import { usePageLoaded } from "../../hooks/usePageLoaded";
import { StickyHeaderProvider } from "../../hooks/useStickyHeader";
import { UserMode, UserModeProvider, useUserMode } from "../../lib/UserMode";
import AccountBanner from "../AccountBanner";
import { AiSummaryBanner } from "../banners/AiSummaryBanner";
import MembershipsBannerGate from "../banners/MembershipsBannerGate";
import UpcomingMaintenanceBanner from "../banners/UpcomingMaintenanceBanner";
import { GiftCardModal } from "../giftCards/GiftCardModal";
import Header from "../nav/Header";
import { MobileFooterTabBar } from "../nav/MobileFooterTabBar";
import ReviewReminderTopNoticeBar from "../ReviewReminderTopNoticeBar";
import TimedTopNoticeBar from "../TimedTopNoticeBar";
import TopNoticeBar from "../TopNoticeBar";
import StickyHeaderConatiner from "./StickyHeaderContainer";

const SelfIdentificationPublicFunding = lazyWithRetry(
  () => import("../banners/SelfIdentificationPublicFunding"),
);

const CategoryNavbar = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "categoryNavbar" */ "../nav/CategoryNavbar"),
);

const Footer = lazyWithRetry(
  // Convert to default export
  () => import(/* webpackChunkName: "footer" */ "../Footer"),
);

const SignupModal = lazyWithRetry(
  () => import(/* webpackChunkName: "signupModal" */ "../SignupModal"),
);

export type SimpleLayoutProps = {
  children?: React.ReactNode;
  error?: string | ApolloError;
  narrowColumn?: boolean;
  narrowPaddedColumn?: boolean;
  grayBackground?: boolean;
  hideFooter?: boolean;
  hideReviewReminder?: boolean;
  hideAccountBanner?: boolean;
  sx?: SxProps;
  contentSx?: React.CSSProperties;
  userMode?: UserMode;
  allowCategoriesNavbar?: boolean;
  hideSignUpModal?: boolean;
  hideMobileFooterTabBar?: boolean;
  showSearchBox?: boolean;
  hideHeaderRight?: boolean;
  customHeader?: React.ReactNode;
};

const useCategoriesNavbar = ({ enabled }: { enabled: boolean }) => {
  const userMode = useUserMode();
  const { isLoggedIn } = useSession();

  const shouldShowCategories = enabled && userMode !== UserMode.Educator;

  return {
    showInHeader: shouldShowCategories && !isLoggedIn,
    showAsNavbar: shouldShowCategories && isLoggedIn,
  };
};

const SimpleLayout = React.forwardRef<HTMLDivElement, SimpleLayoutProps>(
  (
    {
      children,
      error,
      narrowColumn,
      narrowPaddedColumn,
      grayBackground,
      customHeader,
      hideFooter = false,
      hideReviewReminder = false,
      hideAccountBanner = false,
      sx = [],
      contentSx = {},
      userMode = null,
      allowCategoriesNavbar = false,
      hideSignUpModal = false,
      hideMobileFooterTabBar = false,
      showSearchBox = true,
      hideHeaderRight = false,
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const isPageLoaded = usePageLoaded();
    const mobileFooterRef = React.useRef<HTMLElement>(null);
    const bottomOffset =
      mobileFooterRef.current?.getBoundingClientRect().height ?? 0;
    const { showInHeader, showAsNavbar } = useCategoriesNavbar({
      enabled: allowCategoriesNavbar && !customHeader,
    });

    const headerRef = React.useRef<HTMLDivElement>(null);

    return (
      <UserModeProvider value={userMode}>
        <StickyHeaderProvider headerRef={headerRef}>
          <MembershipsBannerGate />
          <StickyHeaderConatiner ref={headerRef}>
            {!!customHeader ? (
              customHeader
            ) : (
              <Header
                showCategoriesNavbar={showInHeader}
                showSearchBox={showSearchBox}
                hideHeaderRight={hideHeaderRight}
              />
            )}
          </StickyHeaderConatiner>
          {showAsNavbar && (
            <CategoryNavbar
              sx={(theme: Theme) => ({
                position: "relative",
                backgroundColor: "common.white",
                display: "none",
                [theme.breakpoints.up("md")]: {
                  display: "block",
                },
                [theme.breakpoints.down("md")]: {
                  borderTop: "1px solid",
                  borderColor: theme.palette.grey[200],
                },
              })}
            />
          )}
          <Box>
            <TopNoticeBar topNotice={error} />
            <TimedTopNoticeBar />
            {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}
            {!!Env.UPCOMING_MAINTENANCE_DATE && <UpcomingMaintenanceBanner />}
            {!hideAccountBanner && <AccountBanner />}
            <SectionStartReminder />
            {!hideReviewReminder && <ReviewReminderTopNoticeBar />}
            <AiSummaryBanner />
          </Box>

          <Box
            sx={[
              {
                minHeight: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                ...((grayBackground || narrowColumn || narrowPaddedColumn) && {
                  backGroundColor: "#FAFAFA",
                }),
                marginBottom: bottomOffset,
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            ref={ref}
          >
            <Box
              sx={(theme: Theme) => ({
                flexGrow: 1,
                ...(narrowColumn && {
                  maxWidth: "750px",
                  position: "relative",
                  width: "100%",
                  marginLeft: "auto",
                  marginRight: "auto",
                  boxSizing: "border-box",
                  backgroundColor: "#FFF",
                }),
                ...(narrowPaddedColumn && {
                  maxWidth: "750px",
                  position: "relative",
                  width: "100%",
                  marginLeft: "auto",
                  marginRight: "auto",
                  boxSizing: "border-box",
                  backgroundColor: "#FFF",
                  paddingY: "2em",
                  paddingX: "1em",
                  [theme.breakpoints.up("md")]: {
                    paddingX: "2em",
                  },
                }),
                ...contentSx,
              })}
            >
              {children}
            </Box>
            <Suspense fallback={null}>
              {isPageLoaded && <SelfIdentificationPublicFunding />}
            </Suspense>
            {!hideMobileFooterTabBar && (
              <MobileFooterTabBar ref={mobileFooterRef} />
            )}
            {!hideFooter && (
              <LazyBox
                placeholder={null}
                rootMargin="150px"
                sx={{}}
                children={
                  <Suspense fallback={null}>
                    <Footer />
                  </Suspense>
                }
              />
            )}
            {!hideSignUpModal && (
              <Suspense fallback={null}>
                <SignupModal />
              </Suspense>
            )}
            <GiftCardModal />
          </Box>
        </StickyHeaderProvider>
      </UserModeProvider>
    );
  },
);

export default SimpleLayout;
